import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import App from './App';
import { NotFoundPage, RouteErrorPage } from './components/ui';
import { CallbackPage, LoginPage, RequireAuth } from './features/auth';
import { AgreementDetailPage, AgreementsPage } from './features/agreements';
import {
  EditInvoicePage,
  InvoiceDetailPage,
  InvoicesListPage,
  NewInvoicePage,
  RecurringInvoicesPage,
} from './features/invoices';
import { TeamDetailPage, TeamListPage } from './features/team';
import { ClientDetailPage, ClientsPage } from './features/clients';
import { DashboardPage } from './features/dashboard';
import { TimePage } from './features/time';

/**
 * A moved screen keeps its query string. `<Navigate to="/x">` drops it, which
 * would turn a bookmarked `/cashflow?year=2025` into "this year" without
 * saying so — a redirect that silently changes what you are looking at is
 * worse than a 404.
 */
function RedirectTo({
  to,
  hash,
  keepSearch = false,
}: {
  to: string;
  hash?: string;
  /** Only where the old params still mean something on the new screen. */
  keepSearch?: boolean;
}) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search: keepSearch ? search : '', hash }} replace />;
}

/**
 * Projects became agreements (the entity rename). Old bookmarks and links in
 * chat history keep working; the path segment after /projects/ is the same id.
 */
function ProjectsToAgreements() {
  const { pathname, search, hash } = useLocation();
  const to = pathname.replace(/^\/projects/, '/agreements');
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

/**
 * Router — scaffold owns this file. Feature agents replace exactly ONE route
 * element (their own) with their real page component; no other edits here.
 * (Auth agent additions: /login, /auth/callback, and the RequireAuth gate.)
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/auth/callback', element: <CallbackPage /> },
  // The client portal (Wave E3) — the app's only CLIENT-facing screen, and the
  // reason it sits out here beside /login rather than under `/`: it must not be
  // wrapped in RequireAuth (clients have no accounts) and must not render the
  // App shell (no rail, no sidebar, no org switcher). It authenticates itself
  // from the magic link in the URL, server-side.
  // Imported from the FILE, not the feature's index: the index also exports the
  // Settings tab, which imports `lib/api` and therefore the auth store. This
  // route's chunk must contain no auth-store code at all — a client's page has
  // no session to read, refresh or clear.
  {
    path: '/portal/hours/:token',
    lazy: async () => ({
      Component: (await import('./features/client-portal/PortalPage')).PortalPage,
    }),
  },
  // The hosted invoice — the URL every invoice email now carries. Same rules
  // as the portal above: outside RequireAuth, outside the App shell, and
  // imported from the FILE so its chunk contains no auth-store code.
  {
    path: '/i/:token',
    lazy: async () => ({
      Component: (await import('./features/invoices/PublicInvoicePage')).PublicInvoicePage,
    }),
  },
  // Paying it. Same token, same rules — and a separate route rather than a
  // modal so the URL a client is emailed lands them on the card form itself.
  {
    path: '/i/:token/pay',
    lazy: async () => ({
      Component: (await import('./features/invoices/HostedInvoicePayPage')).HostedInvoicePayPage,
    }),
  },
  // The shared hours report — a client-facing page, so the hosted invoice's
  // rules: outside RequireAuth and the App shell, imported from the FILE so its
  // chunk carries no auth-store code. The report comes from the token.
  {
    path: '/r/:token',
    lazy: async () => ({
      Component: (await import('./features/hours-report/PublicReportPage')).PublicReportPage,
    }),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <App />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'clients', element: <ClientsPage /> },
      { path: 'clients/:clientId', element: <ClientDetailPage /> },
      { path: 'agreements', element: <AgreementsPage /> },
      { path: 'agreements/:id', element: <AgreementDetailPage /> },
      { path: 'agreements/:id/report', lazy: async () => ({ Component: (await import('./features/hours-report')).HoursReportPage }) },
      { path: 'projects', element: <ProjectsToAgreements /> },
      { path: 'projects/:id', element: <ProjectsToAgreements /> },
      { path: 'time', element: <TimePage /> },
      { path: 'team', element: <TeamListPage /> },
      { path: 'team/:id', element: <TeamDetailPage /> },
      { path: 'invoices', element: <InvoicesListPage /> },
      { path: 'invoices/new', element: <NewInvoicePage /> },
      { path: 'invoices/recurring', element: <RecurringInvoicesPage /> },
      { path: 'invoices/deposits', lazy: async () => ({ Component: (await import('./features/bank')).DepositsPage }) },
      { path: 'invoices/:id', element: <InvoiceDetailPage /> },
      { path: 'invoices/:id/edit', element: <EditInvoicePage /> },
      {
        // /expenses is RETIRED (Jesse, 2026-08-19: the ledger held 0 rows in
        // years of running the business, QuickBooks is the ruled cost source,
        // and the cash-plan tab was a second door onto the same CashExpense
        // rows the Budget & Forecast Expenses tab edits). It was a nav item,
        // so it redirects rather than 404s — same treatment as /budgets.
        path: 'expenses',
        element: <Navigate to="/budget-forecast?tab=budget" replace />,
      },
      { path: 'budget-forecast', lazy: async () => ({ Component: (await import('./features/cashflow')).BudgetForecastPage }) },
      // ONE forward-looking money screen (Jesse, 2026-08-12). /cashflow, the
      // forecast and the sales pipeline were three destinations for one job;
      // they are all this page now. Old URLs redirect — bookmarks, the
      // screenshot script and links written by other features must not 404.
      // /cashflow keeps its query (?year still means the same thing); the
      // other two do not (?view and ?deal describe screens that are gone).
      { path: 'cashflow', element: <RedirectTo to="/budget-forecast" keepSearch /> },
      // /budgets is DELETED, not merged: the account × month grid answered a
      // question nobody asked here. It redirects rather than 404s because it
      // was a nav item until this change, and the nearest true screen is the
      // one that now holds the only target it provided (the annual Goal).
      { path: 'budgets', element: <RedirectTo to="/budget-forecast" /> },
      { path: 'forecast', element: <RedirectTo to="/budget-forecast" hash="projected" /> },
      { path: 'sales-pipeline', element: <RedirectTo to="/budget-forecast" hash="projected" /> },
      { path: 'reports/pnl', lazy: async () => ({ Component: (await import('./features/pnl')).PnlPage }) },
      { path: 'reports/rd', lazy: async () => ({ Component: (await import('./features/research')).ResearchPage }) },
      { path: 'support', lazy: async () => ({ Component: (await import('./features/support')).SupportPage }) },
      { path: 'activity', lazy: async () => ({ Component: (await import('./features/activity')).ActivityPage }) },
      { path: 'settings', lazy: async () => ({ Component: (await import('./features/settings')).SettingsPage }) },
      // Settings → Integrations is an INDEX; each integration is configured on
      // its own screen (Jesse, 2026-08-13). Both are linked from that index and
      // both offer a way back to it. QuickBooks has no route — it is not built,
      // and a URL that can only say "not yet" is a dead end.
      { path: 'settings/integrations/harvest', lazy: async () => ({ Component: (await import('./features/settings')).HarvestIntegrationPage }) },
      { path: 'settings/integrations/stripe', lazy: async () => ({ Component: (await import('./features/settings')).StripeIntegrationPage }) },
      { path: 'settings/integrations/plaid', lazy: async () => ({ Component: (await import('./features/bank')).PlaidIntegrationPage }) },
      { path: 'settings/integrations/quickbooks', lazy: async () => ({ Component: (await import('./features/quickbooks')).QuickBooksIntegrationPage }) },
      { path: 'settings/ecosystem/hub', lazy: async () => ({ Component: (await import('./features/settings')).HubConnectionPage }) },
      { path: 'settings/ecosystem/directory', lazy: async () => ({ Component: (await import('./features/settings')).DirectoryConnectionPage }) },
      { path: 'settings/ecosystem/sales', lazy: async () => ({ Component: (await import('./features/settings')).SalesConnectionPage }) },
      // Harvest sync shipped as a standalone page that no link in the app
      // pointed at, then became a Settings tab, and is now part of the one
      // Harvest screen. The alias stays so bookmarks and the screenshot script
      // keep working — `?tab=harvest-sync` resolves on to the screen.
      { path: 'settings/harvest-sync', element: <Navigate to="/settings?tab=harvest-sync" replace /> },
      // A URL we don't serve says so, inside the shell, instead of silently
      // redirecting to the dashboard as if nothing happened.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
